import { SxProps, Theme } from "@mui/material";

export const gradientText: SxProps<Theme> = {
  background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 60%, #67e8f9 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const eyebrowSx: SxProps<Theme> = {
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "#38bdf8",
  fontFamily: "'Barlow', sans-serif",
  mb: 3,
};

export const glassmorphismCard: SxProps<Theme> = {
  bgcolor: "rgba(5,12,35,0.35)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(56,189,248,0.18)",
  borderRadius: "20px",
  boxShadow: "0 8px 40px rgba(10,15,30,0.3), 0 0 40px rgba(56,189,248,0.08)",
};

export const heroOuter: SxProps<Theme> = {
  position: "relative",
  overflow: "hidden",
  color: "white",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  background: "#000",
};

export const heroVideoIframe: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "max(100%, calc(100vh * 1.7778))",
  height: "max(100%, calc(100vw * 0.5625))",
  transform: "translate(-50%, -50%)",
  zIndex: 0,
  border: 0,
  pointerEvents: "none",
};

export const heroOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "rgba(8,12,20,0.20)",
  zIndex: 1,
  pointerEvents: "none",
};

export const heroVignette: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "radial-gradient(ellipse 75% 65% at 50% 50%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const heroTopGradient: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "180px",
  background: "linear-gradient(to bottom, rgba(5,12,35,0.65) 0%, transparent 100%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const heroBottomGradient: SxProps<Theme> = {
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
  height: "28%",
  background: "linear-gradient(to bottom, rgba(5,12,35,0) 0%, #050c23 100%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const heroEyebrow: SxProps<Theme> = {
  ...eyebrowSx,
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0s",
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(28px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export const heroTitleLine1: SxProps<Theme> = {
  fontSize: { xs: "2rem", md: "3rem", lg: "3.8rem" },
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  fontWeight: 800,
  color: "white",
  mb: 0,
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.2s",
};

export const heroTitleLine2: SxProps<Theme> = {
  fontSize: { xs: "2rem", md: "3rem", lg: "3.8rem" },
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  fontWeight: 800,
  color: "white",
  mb: 7,
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.35s",
};

export const heroPlayButtonOuter: SxProps<Theme> = {
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 1.5,
  mb: 7,
  cursor: "pointer",
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.5s",
  "&:hover .play-ring": {
    boxShadow: "0 0 0 8px rgba(56,189,248,0.2), 0 0 48px rgba(56,189,248,0.45)",
    transform: "scale(1.1)",
  },
};

export const heroPlayRing: SxProps<Theme> = {
  width: 84,
  height: 84,
  borderRadius: "50%",
  border: "2px solid rgba(56,189,248,0.75)",
  background: "rgba(56,189,248,0.10)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  "@keyframes ripple": {
    "0%, 100%": { boxShadow: "0 0 0 0px rgba(56,189,248,0.4), 0 0 24px rgba(56,189,248,0.2)" },
    "50%": { boxShadow: "0 0 0 14px rgba(56,189,248,0), 0 0 36px rgba(56,189,248,0.35)" },
  },
  animation: "ripple 2.5s ease-in-out infinite",
};

export const heroPlayLabel: SxProps<Theme> = {
  fontSize: "0.68rem",
  fontWeight: 600,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.55)",
  fontFamily: "'Barlow', sans-serif",
};

export const heroDescription: SxProps<Theme> = {
  mb: 3,
  fontWeight: 400,
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.75,
  fontSize: { xs: "1rem", md: "1.1rem" },
  maxWidth: 660,
  mx: "auto",
  fontFamily: "'Barlow', sans-serif",
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.65s",
};

export const heroTagline: SxProps<Theme> = {
  mb: 7,
  fontWeight: 700,
  color: "white",
  fontSize: { xs: "1rem", md: "1.2rem" },
  fontFamily: "'Barlow', sans-serif",
  letterSpacing: "0.01em",
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.75s",
};

export const heroCtaButton: SxProps<Theme> = {
  background: "#f97316",
  color: "white",
  borderRadius: "50px",
  px: 5,
  py: 1.75,
  fontSize: "1rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  textTransform: "none",
  "&:hover": {
    background: "#ea6c0a",
    color: "white",
  },
};

export const heroScrollIndicator: SxProps<Theme> = {
  position: "absolute",
  bottom: 40,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.75,
  cursor: "pointer",
  "@keyframes float": {
    "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
    "50%": { transform: "translateX(-50%) translateY(-8px)" },
  },
  animation: "float 2.4s ease-in-out infinite",
  "&:hover": { opacity: 0.7 },
  transition: "opacity 0.2s",
};

export const heroScrollLabel: SxProps<Theme> = {
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.5)",
  fontFamily: "'Barlow', sans-serif",
};

export const videoModal: SxProps<Theme> = {
  position: "fixed",
  inset: 0,
  bgcolor: "rgba(0,0,0,0.94)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const videoModalCloseButton: SxProps<Theme> = {
  position: "absolute",
  top: 20,
  right: 20,
  color: "white",
  bgcolor: "rgba(255,255,255,0.1)",
  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
};

export const videoModalContent: SxProps<Theme> = {
  width: "90vw",
  maxWidth: 1100,
  aspectRatio: "16/9",
  borderRadius: "12px",
  overflow: "hidden",
};

export const statsSection: SxProps<Theme> = {
  py: { xs: 8, md: 12 },
  background: "#050c23",
  borderTop: "1px solid rgba(56,189,248,0.1)",
};

export const statsContainer: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 3,
  justifyContent: "center",
};

export const statCardBase: SxProps<Theme> = {
  position: "relative",
  overflow: "hidden",
  flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 16px)", md: "1 1 0" },
  minWidth: { md: 320 },
  background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.09)",
  borderRadius: "16px",
  p: { xs: 4, md: 5 },
  textAlign: "center",
  "@keyframes cardEntrance": {
    "0%": { opacity: 0, transform: "translateY(40px) scale(0.92)" },
    "60%": { opacity: 1 },
    "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  "&:hover": {
    transform: "translateY(-4px)",
    border: "1px solid rgba(56,189,248,0.25)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(56,189,248,0.08)",
  },
};

export const statCardTopBar: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "4px",
  background: "linear-gradient(90deg, #3b82f6, #38bdf8, #67e8f9)",
  borderRadius: "16px 16px 0 0",
};

export const statCardGlow: SxProps<Theme> = {
  position: "absolute",
  top: -40,
  left: "50%",
  transform: "translateX(-50%)",
  width: 160,
  height: 160,
  borderRadius: "50%",
  background: "radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)",
  pointerEvents: "none",
  filter: "blur(24px)",
};

export const statCardValue: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "2.2rem", md: "2.8rem" },
  background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 60%, #67e8f9 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  lineHeight: 1,
  mb: 1.5,
  whiteSpace: "nowrap",
};

export const statCardLabel: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.55)",
  fontSize: "0.8rem",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

export const whyCardTitle: SxProps<Theme> = {
  fontWeight: 700,
  color: "white",
  mb: 1.5,
  fontFamily: "'Barlow', sans-serif",
};

export const whyCardDescription: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.65,
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.95rem",
};

export const whyJoinOuter: SxProps<Theme> = {
  position: "relative",
  py: { xs: 12, md: 18 },
  backgroundImage: "url('/img/assets/house.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  overflow: "hidden",
};

export const whyJoinOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to bottom, #080c14 0%, rgba(5,12,35,0.18) 30%, rgba(5,12,35,0.18) 70%, #050c23 100%)",
  zIndex: 0,
  pointerEvents: "none",
};

export const whyJoinVignette: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(5,12,35,0.72) 0%, transparent 100%)",
  zIndex: 0,
  pointerEvents: "none",
};

export const whyJoinSectionTitle: SxProps<Theme> = {
  color: "white",
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4.5rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
  mb: 3,
};

export const whyJoinSectionSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: { xs: "1rem", md: "1.15rem" },
  lineHeight: 1.7,
  maxWidth: 640,
  mb: 6,
  mx: "auto",
  textAlign: "center",
};

export const scaleImpactOuter: SxProps<Theme> = {
  py: { xs: 12, md: 18 },
  background: "linear-gradient(to bottom, #040922 0%, #080f1e 50%, #050c23 100%)",
  position: "relative",
  overflow: "hidden",
};

export const scaleImpactGlow: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "800px",
  height: "800px",
  borderRadius: "50%",
  background: "radial-gradient(circle, rgba(56,189,248,0.07) 0%, rgba(56,189,248,0.02) 50%, transparent 70%)",
  pointerEvents: "none",
};

export const scaleImpactTitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "1.9rem", md: "2.6rem", lg: "3rem" },
  color: "white",
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  mb: 3,
  maxWidth: "860px",
  mx: "auto",
};

export const scaleImpactSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: { xs: "1rem", md: "1.1rem" },
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.75,
  maxWidth: "580px",
  mx: "auto",
  mb: { xs: 8, md: 10 },
};

export const aiCardOuter: SxProps<Theme> = {
  flex: 1,
  p: { xs: 4, md: 5 },
  borderRadius: "20px",
  background: "rgba(255,255,255,0.03)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(56,189,248,0.15)",
  boxShadow: "0 0 40px rgba(56,189,248,0.04), inset 0 1px 0 rgba(255,255,255,0.06)",
  textAlign: "left",
  position: "relative",
  overflow: "hidden",
};

export const aiCardGlow: SxProps<Theme> = {
  position: "absolute",
  top: -40,
  left: -40,
  width: "180px",
  height: "180px",
  borderRadius: "50%",
  background: "radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)",
  pointerEvents: "none",
};

export const aiCardIconBox: SxProps<Theme> = {
  width: 48,
  height: 48,
  borderRadius: "14px",
  background: "rgba(56,189,248,0.1)",
  border: "1px solid rgba(56,189,248,0.25)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 3,
  "& svg": { fontSize: 22, color: "#38bdf8" },
};

export const aiCardTitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: "1.05rem",
  color: "white",
  mb: 1.5,
};

export const aiCardBody: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.95rem",
  color: "rgba(255,255,255,0.55)",
  lineHeight: 1.65,
};

export const howItWorksOuter: SxProps<Theme> = {
  py: { xs: 12, md: 18 },
  background: "linear-gradient(to bottom, #050c23 0%, #0a0f1e 100%)",
  position: "relative",
};

export const howItWorksTopFade: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "120px",
  background: "linear-gradient(to bottom, #050c23 0%, transparent 100%)",
  pointerEvents: "none",
  zIndex: 0,
};

export const howItWorksTitle: SxProps<Theme> = {
  color: "white",
  fontSize: { xs: "2rem", md: "3rem", lg: "3.8rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
  maxWidth: { md: "540px", lg: "660px" },
  mx: "auto",
};

export const howItWorksStepsContainer: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: { xs: 4, md: 0 },
  position: "relative",
};

export const howItWorksConnector: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
  position: "absolute",
  top: "40px",
  left: "16.66%",
  right: "16.66%",
  height: "1px",
  background: "linear-gradient(to right, rgba(56,189,248,0.15), rgba(56,189,248,0.35), rgba(56,189,248,0.15))",
  zIndex: 0,
};

export const stepNumber: SxProps<Theme> = {
  fontSize: { xs: "3.5rem", md: "4.5rem" },
  fontWeight: 800,
  fontFamily: "'Barlow', sans-serif",
  lineHeight: 1,
  mb: 2,
  ...gradientText,
};

export const stepTitle: SxProps<Theme> = {
  fontWeight: 700,
  color: "white",
  mb: 1.5,
  fontFamily: "'Barlow', sans-serif",
};

export const stepDescription: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.65,
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.95rem",
};

export const testimonialsSectionTitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4.5rem" },
  color: "white",
  lineHeight: 1.15,
  mb: 2,
};

export const testimonialsSectionSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: { xs: "1rem", md: "1.1rem" },
  lineHeight: 1.7,
};

export const testimonialNavButton: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 10,
  width: 48,
  height: 48,
  bgcolor: "rgba(5,12,35,0.7)",
  border: "1px solid rgba(59,130,246,0.3)",
  backdropFilter: "blur(8px)",
  color: "#38bdf8",
  "&:hover": { bgcolor: "rgba(59,130,246,0.2)", borderColor: "rgba(59,130,246,0.6)" },
  transition: "all 0.2s ease",
};

export const testimonialCardOuter: SxProps<Theme> = {
  background: "rgba(5,12,35,0.55)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(59,130,246,0.4)",
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "0 24px 80px rgba(0,0,0,0.5), 0 0 60px rgba(59,130,246,0.12)",
};

export const testimonialThumbnailContainer: SxProps<Theme> = {
  position: "relative",
  aspectRatio: "16/9",
  overflow: "hidden",
  background: "#0a0f1e",
};

export const testimonialThumbnailImage: SxProps<Theme> = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

export const testimonialHeadline: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  color: "white",
  fontSize: "1rem",
  lineHeight: 1.5,
  mb: 1.5,
};

export const testimonialQuote: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.7)",
  fontSize: "0.9rem",
  lineHeight: 1.7,
  fontStyle: "italic",
};

export const testimonialAvatarCircle: SxProps<Theme> = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const testimonialName: SxProps<Theme> = {
  color: "white",
  fontWeight: 700,
  fontSize: "0.95rem",
  fontFamily: "'Barlow', sans-serif",
};

export const testimonialRole: SxProps<Theme> = {
  color: "rgba(255,255,255,0.45)",
  fontSize: "0.8rem",
  fontFamily: "'Barlow', sans-serif",
};

export const testimonialTrack: SxProps<Theme> = {
  display: "flex",
  transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
  gap: "24px",
  alignItems: "center",
};

export const finalCtaOuter: SxProps<Theme> = {
  py: { xs: 16, md: 24 },
  minHeight: { xs: "60vh", md: "75vh" },
  display: "flex",
  alignItems: "center",
  textAlign: "center",
  background: "linear-gradient(to bottom, #050c23, #0a0f1e)",
  position: "relative",
  overflow: "hidden",
};

export const finalCtaVideo: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  zIndex: 0,
};

export const finalCtaOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "rgba(8,12,20,0.30)",
  zIndex: 1,
  pointerEvents: "none",
};

export const finalCtaVignette: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0) 75%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const finalCtaTitle: SxProps<Theme> = {
  color: "white",
  mb: 2,
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4.5rem" },
  letterSpacing: "-0.02em",
  fontWeight: 800,
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
};

export const finalCtaSubtitle: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  mb: 5,
  fontWeight: 400,
  fontSize: { xs: "1rem", md: "1.15rem" },
  lineHeight: 1.7,
  fontFamily: "'Barlow', sans-serif",
};

export const finalCtaSellerButton: SxProps<Theme> = {
  background: "#f97316",
  color: "white",
  borderRadius: "50px",
  px: 5,
  py: 1.75,
  fontSize: "1rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  "&:hover": {
    background: "#ea6c0a",
    color: "white",
  },
};

export const finalCtaEnterpriseButton: SxProps<Theme> = {
  color: "white",
  bgcolor: "rgba(0,0,0,0.15)",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: "50px",
  px: 4,
  py: 1.5,
  "&:hover": {
    borderColor: "rgba(255,255,255,0.5)",
    bgcolor: "rgba(0,0,0,0.25)",
    color: "white",
  },
};

export const rewardOuter: SxProps<Theme> = {
  position: "relative",
  background: "linear-gradient(to bottom, #050c23, #080c14 30%, #0d1526)",
  py: { xs: 10, md: 14 },
};

export const rewardBottomGradient: SxProps<Theme> = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "320px",
  background: "linear-gradient(to top, #040817 0%, rgba(4,8,23,0) 100%)",
  pointerEvents: "none",
  zIndex: 0,
};

export const rewardEyebrowAnimated: SxProps<Theme> = {
  ...eyebrowSx,
  opacity: 0,
  "@keyframes copySlideIn": {
    from: { opacity: 0, transform: "translateX(-24px)" },
    to: { opacity: 1, transform: "translateX(0)" },
  },
};

export const rewardTitle: SxProps<Theme> = {
  fontSize: { xs: "3rem", md: "4.5rem", lg: "5.5rem" },
  letterSpacing: "-0.03em",
  lineHeight: 1.1,
  fontWeight: 800,
  fontFamily: "'Poppins', sans-serif",
  mb: 4,
  opacity: 0,
};

export const rewardDescription: SxProps<Theme> = {
  mb: 3,
  fontWeight: 400,
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.7,
  fontSize: { xs: "1rem", md: "1.15rem" },
  fontFamily: "'Barlow', sans-serif",
  maxWidth: 520,
  opacity: 0,
};

export const rewardStatementsContainer: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
  opacity: 0,
};

export const rewardCheckIcon: SxProps<Theme> = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "rgba(59,130,246,0.15)",
  border: "1px solid rgba(59,130,246,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 0 10px rgba(59,130,246,0.35)",
};

export const rewardStatementText: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  fontSize: { xs: "1rem", md: "1.05rem" },
  fontFamily: "'Barlow', sans-serif",
  lineHeight: 1.5,
};

export const rewardCardContainer: SxProps<Theme> = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: { xs: 280, md: 460 },
  opacity: 0,
};

export const rewardCardGlow: SxProps<Theme> = {
  position: "absolute",
  width: "70%",
  height: "70%",
  borderRadius: "50%",
  background:
    "radial-gradient(ellipse at center, rgba(56,189,248,0.18) 0%, rgba(59,130,246,0.08) 50%, transparent 75%)",
  filter: "blur(40px)",
  pointerEvents: "none",
};

export const rewardCard: SxProps<Theme> = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "20px",
  background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(56,189,248,0.25)",
  boxShadow: "0 8px 40px rgba(10,15,30,0.6), 0 0 80px rgba(56,189,248,0.10)",
  p: { xs: 5, md: 7 },
  textAlign: "center",
  width: "100%",
};

export const rewardCardTopBar: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "3px",
  background: "linear-gradient(90deg, #3b82f6, #38bdf8, #67e8f9)",
};

export const rewardCardGlowInner: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 280,
  height: 280,
  borderRadius: "50%",
  background: "radial-gradient(circle, rgba(56,189,248,0.09) 0%, transparent 70%)",
  pointerEvents: "none",
  filter: "blur(32px)",
};

export const rewardCardValue: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "3.5rem", md: "4.5rem", lg: "5.5rem" },
  lineHeight: 0.9,
  background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  position: "relative",
  mb: 3,
};

export const rewardCardLabel: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "0.85rem", md: "1rem" },
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.55)",
  position: "relative",
};

export const provenAtScaleOuter: SxProps<Theme> = {
  position: "relative",
  minHeight: { md: "90vh" },
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: { xs: "stretch", md: "center" },
  backgroundImage: "url('/img/assets/24.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  overflow: "hidden",
};

export const provenAtScaleRightOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to right, rgba(5,12,35,0.0) 0%, rgba(5,12,35,0.2) 50%, rgba(5,12,35,0.5) 100%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const provenAtScaleTopFade: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "28%",
  background: "linear-gradient(to bottom, #040817 0%, transparent 100%)",
  zIndex: 2,
  pointerEvents: "none",
};

export const provenAtScaleBottomFade: SxProps<Theme> = {
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
  height: "28%",
  background: "linear-gradient(to top, #040817 0%, transparent 100%)",
  zIndex: 2,
  pointerEvents: "none",
};

export const provenAtScaleContentAnimated: SxProps<Theme> = {
  textAlign: "left",
  pl: { md: 2, lg: 3 },
  opacity: 0,
  "@keyframes copySlideIn": {
    from: { opacity: 0, transform: "translateX(-24px)" },
    to: { opacity: 1, transform: "translateX(0)" },
  },
};

export const provenAtScaleTitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "3rem", md: "4rem", lg: "4.8rem" },
  color: "white",
  lineHeight: 1.1,
  mb: 3,
};

export const provenAtScaleDescription: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: { xs: "rgba(255,255,255,0.92)", md: "rgba(255,255,255,0.82)" },
  fontSize: { xs: "1rem", md: "1.1rem" },
  lineHeight: 1.7,
  mb: 4,
  p: { xs: 2, md: 0 },
  background: { xs: "rgba(29, 29, 32, 0.36)", md: "transparent" },
  backdropFilter: { xs: "blur(8px)", md: "none" },
  WebkitBackdropFilter: { xs: "blur(8px)", md: "none" },
  borderRadius: { xs: 2, md: 0 },
  border: { xs: "1px solid rgba(255,255,255,0.12)", md: "none" },
};

export const provenAtScaleJoinButton: SxProps<Theme> = {
  background: "#f97316",
  color: "white",
  fontFamily: "'Barlow', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  px: 4,
  py: 1.5,
  borderRadius: 24,
  textTransform: "none",
  boxShadow: "0 0 24px rgba(249,115,22,0.4)",
  "&:hover": {
    background: "#ea6c0a",
    boxShadow: "0 0 36px rgba(249,115,22,0.6)",
    color: "white",
  },
};

export const provenAtScaleBadgesContainer: SxProps<Theme> = {
  position: { xs: "static", md: "absolute" },
  bottom: { md: 24 },
  left: { md: 64 },
  px: { xs: 3, md: 0 },
  pb: { xs: 6, md: 0 },
  zIndex: 4,
};

export const provenAtScaleBadgesInner: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 2,
  background: "rgba(255,255,255,0.08)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  borderRadius: 2,
  px: 2,
  py: 1.5,
  border: "1px solid rgba(255,255,255,0.12)",
};

export const provenAtScaleBadgesInnerMobile: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  alignItems: "center",
  justifyItems: "center",
  gap: 2,
  background: "rgba(255,255,255,0.08)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  borderRadius: 2,
  px: 2,
  py: 1.5,
  border: "1px solid rgba(255,255,255,0.12)",
};

export const provenAtScaleBadgeImage: SxProps<Theme> = {
  height: { xs: 64, md: 80 },
  width: "auto",
  objectFit: "contain",
};

export const smartPlatformOuter: SxProps<Theme> = {
  position: "relative",
  background: "linear-gradient(to bottom, #040817, #080c14)",
  py: { xs: 10, md: 16 },
};

export const smartPlatformTopFade: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "280px",
  background: "linear-gradient(to bottom, #040817 0%, rgba(4,8,23,0) 100%)",
  pointerEvents: "none",
  zIndex: 0,
};

export const smartPlatformContentAnimated: SxProps<Theme> = {
  textAlign: "left",
  opacity: 0,
  "@keyframes copySlideIn": {
    from: { opacity: 0, transform: "translateX(-24px)" },
    to: { opacity: 1, transform: "translateX(0)" },
  },
};

export const smartPlatformTitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: "clamp(1.6rem, 3.2vw, 3.8rem)",
  color: "white",
  lineHeight: 1.15,
  mb: 3,
};

export const smartPlatformDescription: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: { xs: "1rem", md: "1.15rem" },
  lineHeight: 1.7,
  maxWidth: "72%",
};

export const smartPlatformCheckItem: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: { xs: "0.95rem", md: "1rem" },
  lineHeight: 1.5,
};

export const smartPlatformVideoIframe: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  border: "none",
};

export const technologySectionSticky: SxProps<Theme> = {
  position: { xs: "static", md: "sticky" },
  top: 0,
  height: { xs: "auto", md: "100vh" },
  py: { xs: 10, md: 0 },
  display: "flex",
  overflow: "hidden",
  background: "#0a0f1e",
};

export const technologyLeftPanel: SxProps<Theme> = {
  width: { xs: "100%", md: "50%" },
  display: "flex",
  alignItems: "center",
  px: { xs: 3, md: 8 },
};

export const technologyEyebrow: SxProps<Theme> = {
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.2em",
  color: "#38bdf8",
  fontFamily: "'Barlow', sans-serif",
  mb: 2,
  textTransform: "uppercase",
};

export const technologyTitle: SxProps<Theme> = {
  fontSize: { xs: "3rem", md: "4.5rem", lg: "5.5rem" },
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontWeight: 800,
  mb: 3,
};

export const technologyDescription: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  fontSize: "1.1rem",
  lineHeight: 1.65,
  fontFamily: "'Barlow', sans-serif",
  mb: 4,
};

export const technologyFeatureTitle: SxProps<Theme> = {
  color: "white",
  fontSize: "1.125rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  lineHeight: 1.3,
  mb: 0.4,
};

export const technologyFeatureDescription: SxProps<Theme> = {
  color: "rgba(255,255,255,0.75)",
  fontSize: "1rem",
  fontFamily: "'Barlow', sans-serif",
  lineHeight: 1.5,
};

export const technologyRightPanel: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
  width: "50%",
  position: "relative",
  overflow: "hidden",
};

export const technologyRightPanelGlow: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  height: "100%",
  background:
    "radial-gradient(ellipse at center, rgba(59,130,246,0.22) 0%, rgba(56,189,248,0.12) 40%, transparent 72%)",
  pointerEvents: "none",
  zIndex: 0,
  filter: "blur(50px)",
};

export const technologyProposalImage: SxProps<Theme> = {
  display: "block",
  width: "100%",
  maxHeight: "94vh",
  borderRadius: "12px",
  boxShadow: "0 32px 100px rgba(0,0,0,0.9), 0 0 80px rgba(59,130,246,0.1)",
  userSelect: "none",
  pointerEvents: "none",
};

export const technologyCarouselRow: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: "center",
  gap: { xs: 8, md: 10 },
};

export const technologyCarouselControls: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 1,
  mb: 2,
};

export const technologyCarouselArrow: SxProps<Theme> = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.07)",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "white",
  "&:hover": {
    background: "rgba(56,189,248,0.2)",
    border: "1px solid rgba(56,189,248,0.4)",
  },
};

export const technologyCarouselImage: SxProps<Theme> = {
  display: "block",
  width: "100%",
  borderRadius: "12px",
  boxShadow: "0 32px 100px rgba(0,0,0,0.9), 0 0 80px rgba(59,130,246,0.1)",
  userSelect: "none",
};

export const technologyCarouselDots: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  gap: "8px",
  mt: 3,
};

export const technologyCarouselDot: SxProps<Theme> = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  cursor: "pointer",
  transition: "background 0.3s ease",
};

export const invitationOuter: SxProps<Theme> = {
  py: { xs: 12, md: 18 },
  background: "linear-gradient(to bottom, #0a0f1e 0%, #050c23 50%, #0a0f1e 100%)",
  position: "relative",
  overflow: "hidden",
};

export const invitationTopFade: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "160px",
  background: "linear-gradient(to bottom, #0a0f1e 0%, transparent 100%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const invitationBottomFade: SxProps<Theme> = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "220px",
  background: "linear-gradient(to top, #0a0f1e 0%, transparent 100%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const invitationEyebrow: SxProps<Theme> = {
  fontSize: { xs: "0.7rem", md: "0.75rem" },
  fontWeight: 600,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  mb: 4,
  fontFamily: "'Barlow', sans-serif",
};

export const invitationTitle: SxProps<Theme> = {
  fontSize: { xs: "2.6rem", md: "4rem", lg: "4.8rem" },
  fontWeight: 800,
  fontFamily: "'Poppins', sans-serif",
  letterSpacing: "-0.03em",
  lineHeight: 1.08,
  color: "white",
  mb: 3,
};

export const invitationSubtitle: SxProps<Theme> = {
  fontSize: { xs: "1rem", md: "1.15rem" },
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.7,
  whiteSpace: { xs: "normal", md: "nowrap" },
  mx: "auto",
  mb: { xs: 8, md: 10 },
  fontFamily: "'Barlow', sans-serif",
};

export const invitationCardsContainer: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: 0,
  position: "relative",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "0 2px 40px rgba(0,0,0,0.4)",
};

export const invitationSellerCard: SxProps<Theme> = {
  flex: 1,
  p: { xs: 5, md: 7 },
  display: "flex",
  flexDirection: "column",
  alignItems: { xs: "center", md: "flex-start" },
  textAlign: { xs: "center", md: "left" },
  textDecoration: "none",
  background: "rgba(255,255,255,0.03)",
  transition: "background 0.35s ease, transform 0.35s ease, box-shadow 0.35s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.06)",
    transform: "translateY(-2px) scale(1.005)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
    "& .inv-btn": {
      background: "white",
      color: "#0A0A0A",
    },
  },
};

export const invitationCardEyebrowSeller: SxProps<Theme> = {
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "#38bdf8",
  mb: 3,
  fontFamily: "'Barlow', sans-serif",
};

export const invitationCardTitle: SxProps<Theme> = {
  fontSize: { xs: "1.6rem", md: "2rem" },
  fontWeight: 700,
  fontFamily: "'Poppins', sans-serif",
  color: "white",
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
  mb: 3,
};

export const invitationCardList: SxProps<Theme> = {
  listStyle: "none",
  p: 0,
  m: 0,
  mb: 5,
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  alignItems: "flex-start",
  alignSelf: { xs: "center", md: "stretch" },
};

export const invitationCheckCircle: SxProps<Theme> = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "rgba(56,189,248,0.1)",
  border: "1px solid rgba(56,189,248,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const invitationSellerButton: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  px: 4,
  py: 1.5,
  borderRadius: "50px",
  border: "1px solid rgba(56,189,248,0.5)",
  color: "#38bdf8",
  fontSize: "0.85rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  transition: "background 0.3s ease, color 0.3s ease",
  background: "transparent",
  mt: "auto",
  fontFamily: "'Barlow', sans-serif",
};

export const invitationDividerVertical: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
  width: "1px",
  background:
    "linear-gradient(to bottom, transparent, rgba(255,255,255,0.07) 30%, rgba(56,189,248,0.15) 50%, rgba(255,255,255,0.07) 70%, transparent)",
  flexShrink: 0,
};

export const invitationDividerHorizontal: SxProps<Theme> = {
  display: { xs: "block", md: "none" },
  height: "1px",
  background:
    "linear-gradient(to right, transparent, rgba(255,255,255,0.07) 30%, rgba(56,189,248,0.15) 50%, rgba(255,255,255,0.07) 70%, transparent)",
};

export const invitationEnterpriseCard: SxProps<Theme> = {
  flex: 1,
  p: { xs: 5, md: 7 },
  display: "flex",
  flexDirection: "column",
  alignItems: { xs: "center", md: "flex-start" },
  textAlign: { xs: "center", md: "left" },
  textDecoration: "none",
  background: "rgba(255,255,255,0.03)",
  transition: "background 0.35s ease, transform 0.35s ease, box-shadow 0.35s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.06)",
    transform: "translateY(-2px) scale(1.005)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
    "& .inv-btn": {
      background: "white",
      color: "#0A0A0A",
    },
  },
};

export const invitationCardEyebrowEnterprise: SxProps<Theme> = {
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "#51F2CB",
  mb: 3,
  fontFamily: "'Barlow', sans-serif",
};

export const invitationEnterpriseButton: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  px: 4,
  py: 1.5,
  borderRadius: "50px",
  border: "1px solid rgba(81,242,203,0.5)",
  color: "#51F2CB",
  fontSize: "0.85rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  transition: "background 0.3s ease, color 0.3s ease",
  background: "transparent",
  mt: "auto",
  fontFamily: "'Barlow', sans-serif",
  textDecoration: "none",
  "&:hover": { background: "rgba(81,242,203,0.08)" },
};

export const pricingCard: SxProps<Theme> = {
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: 520,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(56,189,248,0.2)",
  borderRadius: "24px",
  boxShadow: "0 0 80px rgba(56,189,248,0.08), 0 2px 40px rgba(0,0,0,0.4)",
  p: { xs: 4, md: 6 },
};

export const pricingCardGlowTopLeft: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "60%",
  height: "60%",
  background: "radial-gradient(ellipse at top left, rgba(56,189,248,0.13) 0%, transparent 70%)",
  pointerEvents: "none",
};

export const pricingCardGlowBottomRight: SxProps<Theme> = {
  position: "absolute",
  bottom: 0,
  right: 0,
  width: "50%",
  height: "50%",
  background: "radial-gradient(ellipse at bottom right, rgba(32,194,241,0.09) 0%, transparent 70%)",
  pointerEvents: "none",
};

export const pricingBadge: SxProps<Theme> = {
  display: "inline-block",
  background: "rgba(56,189,248,0.1)",
  border: "1px solid rgba(56,189,248,0.25)",
  borderRadius: "50px",
  px: 2,
  py: 0.5,
  mb: 3,
};

export const pricingBadgeText: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "#38bdf8",
};

export const pricingValue: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "2.8rem", md: "3.8rem" },
  lineHeight: 1,
  mb: 0.5,
  ...gradientText,
};

export const pricingValueSuffix: SxProps<Theme> = {
  fontSize: "1.2rem",
  fontWeight: 600,
  WebkitTextFillColor: "rgba(255,255,255,0.5)",
};

export const pricingSubtext: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.5)",
  fontSize: "0.9rem",
  mb: 4,
};

export const pricingFeatureList: SxProps<Theme> = {
  borderTop: "1px solid rgba(255,255,255,0.07)",
  borderBottom: "1px solid rgba(255,255,255,0.07)",
  py: 3,
  mb: 4,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

export const pricingFeatureText: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: "0.95rem",
};

export const pricingCtaButton: SxProps<Theme> = {
  background: "linear-gradient(90deg, #f97316, #fb923c)",
  borderRadius: "50px",
  py: 1.75,
  fontSize: "1rem",
  fontWeight: 700,
  color: "white",
  fontFamily: "'Barlow', sans-serif",
  textTransform: "none",
  boxShadow: "0 4px 24px rgba(249,115,22,0.35)",
  "&:hover": { background: "linear-gradient(90deg, #ea6c0a, #f97316)", color: "white" },
};

export const scrollIndicator: SxProps<Theme> = {
  position: "absolute",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.75,
  cursor: "pointer",
  "@keyframes float": {
    "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
    "50%": { transform: "translateX(-50%) translateY(-8px)" },
  },
  animation: "float 2.4s ease-in-out infinite",
  "&:hover": { opacity: 0.7 },
  transition: "opacity 0.2s",
};

export const scrollLabel: SxProps<Theme> = {
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.5)",
  fontFamily: "'Barlow', sans-serif",
};
